import clsx from 'clsx'
import {cloneDeep, difference, isEmpty} from 'lodash'
import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {useColumnOrder, useSortBy, useTable} from 'react-table'
import {InputCheckNoFormik, PaginateWidget} from '..'
import {MenuComponent} from '../../../assets/ts/components'
import {KTSVG, useDisabled} from '../../../helpers'
import PageSize from '../paginate/PageSize'
import {PaginateModel} from '../paginate/core/_models'
import {CustomsColumnTable} from './CustomsColumnTable'
import {useStatesGlobalNoStorage} from '../../../context/StateContext'
import {reducerCases} from '../../../context/constants'
import {LAPTOP_MIN_WIDTH, TABLE_KEY_COLUMN} from '../../../constants'
import useWindowDimensions from '../../../hooks/useWindowDimensions'

type Props = {
  columns: any
  data: any
  pagination?: any
  usePagination?: boolean
  useCheckBox?: boolean
  useSelectAll?: boolean
  disabledCheckBox?: boolean
  useHeaderUpperCase?: boolean
  useSort?: boolean
  callBackSetData?: (selected: any) => void
  tableClass?: any
  tbodyClass?: any
  theadClass?: any
  removeClassDefaultHeader?: boolean
  customColumn?: {show: boolean; name: string}
  highlightRowIds?: string[]
  isInModal?: boolean
}

export type TableForwardRefProps = {
  scrollToBottom: () => void
}

const Table = forwardRef<TableForwardRefProps, Props>(
  (
    {
      columns,
      data,
      pagination,
      usePagination = false,
      useCheckBox = false,
      useSelectAll = true,
      disabledCheckBox = false,
      useHeaderUpperCase = true,
      useSort = true,
      callBackSetData,
      tableClass,
      tbodyClass,
      theadClass,
      removeClassDefaultHeader = false,
      customColumn = {show: false, name: ''},
      highlightRowIds = [],
      isInModal = false,
    },
    ref
  ) => {
    const intl = useIntl()
    const {width} = useWindowDimensions()
    const {pageDisabled} = useDisabled()
    const [{isShowCustomColumns}, dispatch] = useStatesGlobalNoStorage()
    const [searchParams, setSearchParams] = useSearchParams()

    const initialState = useMemo(() => {
      const sortBy = searchParams.get('sort_by')
      const sortDirection = searchParams.get('sort_direction')
      return {
        hiddenColumns: ['canCheck'],
        sortBy: sortBy ? [{id: sortBy, desc: sortDirection === 'desc'}] : [],
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const [paginationInfo, setPaginationInfo] = useState<PaginateModel>()
    const [initRows, setInitRows] = useState<any>([])
    const [masterChecked, setMasterChecked] = useState(false)
    const [selectedRows, setSelectedRows] = useState<any>([])

    const bottomRef = useRef<HTMLDivElement>(null)

    const scrollToBottom = () => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({
          behavior: 'smooth',
        })
      }
    }

    useImperativeHandle(ref, () => ({
      scrollToBottom,
    }))

    const columnsShow = useMemo(() => {
      if (customColumn.show) {
        return cloneDeep(columns).filter((item) => item?.show)
      } else {
        return columns
      }
    }, [columns, customColumn.show])

    const {
      headerGroups,
      rows,
      prepareRow,
      state: {sortBy},
    } = useTable(
      {
        columns: columnsShow,
        data,
        initialState,
        manualSortBy: true,
        disableSortBy: !useSort,
      },
      useSortBy,
      useColumnOrder
    )

    useEffect(() => {
      if (!isEmpty(pagination)) {
        setPaginationInfo(pagination)
      }
    }, [pagination])

    useEffect(() => {
      if (!isEmpty(rows)) {
        let tempRowsCanCheck = rows.filter((row) => row.values.canCheck)
        setInitRows(tempRowsCanCheck)
      } else {
        setInitRows({})
      }
    }, [rows])

    useEffect(() => {
      if (!isEmpty(selectedRows) && selectedRows.length === initRows.length) {
        setMasterChecked(true)
      }
      if (callBackSetData) {
        callBackSetData(selectedRows)
      }
    }, [callBackSetData, initRows, selectedRows])

    useEffect(() => {
      if (pageDisabled) {
        setMasterChecked(false)
        setSelectedRows([])
      }
    }, [pageDisabled])

    const filtered = (arr: any) => {
      return arr.filter((item: any, index: number) => !arr.includes(item, index + 1))
    }

    const removeElementArray = (arr: any, value: any) => {
      return arr.filter((elm: any) => elm.id !== value.id)
    }

    const handleSelect = (row: any) => {
      if (selectedRows.find((item: any) => item.id === row.id)) {
        setSelectedRows(removeElementArray(selectedRows, row))
        setMasterChecked(false)
      } else {
        setSelectedRows([...selectedRows, row])
      }
    }

    const handleSelectAll = () => {
      let tempRows = selectedRows
      if (!masterChecked) {
        tempRows = initRows
      } else if (masterChecked && !isEmpty(tempRows)) {
        tempRows = difference(tempRows, selectedRows)
      }
      setSelectedRows([...filtered(tempRows)])
      setMasterChecked(!masterChecked)
    }

    const handlePageSizeChange = (pageSize: any) => {
      pagination.page = 1
      pagination.page_size = pageSize.value
      setPaginationInfo(pagination)
      searchParams.set('page', '1')
      searchParams.set('page_size', String(pageSize.value))
      setSearchParams(searchParams, {replace: true})
    }

    const handlePageChange = (page: number) => {
      pagination.page = page
      setPaginationInfo(pagination)
      searchParams.set('page', page.toString())
      setSearchParams(searchParams, {replace: true})
    }

    useEffect(() => {
      if (sortBy.length > 0) {
        if (sortBy[0].desc) {
          searchParams.set('sort_direction', 'desc')
        } else {
          searchParams.set('sort_direction', 'asc')
        }
        searchParams.set('sort_by', sortBy[0].id)
      } else {
        searchParams.delete('sort_direction')
        searchParams.delete('sort_by')
      }
      setSearchParams(searchParams, {replace: true})
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy])

    useEffect(() => {
      if (rows?.length === 0) {
        return
      }
      setTimeout(() => {
        MenuComponent.reinitialization()
      }, 500)
    }, [rows])

    const elementCustomsColumn = useMemo(() => {
      return customColumn.show ? (
        <th
          className={'text-center fixed-column'}
          onClick={() =>
            dispatch({type: reducerCases.IS_SHOW_CUSTOM_COLUMNS, isShowCustomColumns: true})
          }
        >
          <KTSVG
            path={`/media/gori/tables/custom-column-${
              isShowCustomColumns ? 'active' : 'default'
            }.svg`}
            className={clsx('svg-icon svg-icon-8 p-2 rounded-2 cursor-pointer bg-hover-light', {
              'border border-2 border-primary': isShowCustomColumns,
            })}
          />
        </th>
      ) : (
        <th></th>
      )
    }, [customColumn.show, dispatch, isShowCustomColumns])

    return (
      <>
        {/* begin::Body */}
        <CustomsColumnTable
          name={customColumn.name}
          show={isShowCustomColumns}
          initColumns={cloneDeep(columns)}
          columns={cloneDeep(columnsShow)}
          handleClose={() => {
            dispatch({type: reducerCases.IS_SHOW_CUSTOM_COLUMNS, isShowCustomColumns: false})
          }}
        />
        {isEmpty(headerGroups) && (
          <div className='d-flex justify-content-end'>
            <div
              className='btn btn-sm btn-advanced-primary'
              onClick={() =>
                dispatch({type: reducerCases.IS_SHOW_CUSTOM_COLUMNS, isShowCustomColumns: true})
              }
            >
              {intl.formatMessage({id: 'MANAGE_COLUMNS'})}
            </div>
          </div>
        )}
        <div
          className='dataTables_wrapper dt-bootstrap4 no-footer d-flex flex-column flex-fill'
          style={{overflowX: 'auto'}}
        >
          {/* begin::Table container */}
          {/* begin::Table */}
          <div className='flex-fill scroll-y-clip'>
            <table
              className={clsx('table align-middle table-row-dashed fs-6 gy-4 dataTable no-footer', {
                'opacity-50': pageDisabled,
                [tableClass]: !!tableClass,
              })}
            >
              <thead className={theadClass}>
                {headerGroups.map((headerGroup, index) => (
                  <tr
                    key={index}
                    className={
                      removeClassDefaultHeader
                        ? ''
                        : clsx('text-start text-muted fw-bolder fs-7 gs-0', {
                            'text-uppercase': useHeaderUpperCase,
                          })
                    }
                  >
                    {useCheckBox && (
                      <th className='w-10px pe-2 ps-4'>
                        {useSelectAll && initRows.length > 0 && (
                          <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
                            <InputCheckNoFormik
                              checked={masterChecked}
                              onChange={handleSelectAll}
                            />
                          </div>
                        )}
                      </th>
                    )}
                    {headerGroup.headers.map((column, index) => {
                      const checkNoSubActions =
                        index === headerGroup.headers.length - 1 &&
                        headerGroup.headers[index].id !== TABLE_KEY_COLUMN.SUB_ACTIONS

                      if (column.id === TABLE_KEY_COLUMN.SUB_ACTIONS) {
                        return <React.Fragment key={index}>{elementCustomsColumn}</React.Fragment>
                      }

                      return (
                        <React.Fragment key={index}>
                          <th
                            {...column.getHeaderProps([
                              {
                                //@ts-ignore
                                colSpan: column.headerColSpan,
                                className: column.headerClassName,
                              },
                              column.getSortByToggleProps(),
                            ])}
                          >
                            {column.render('Header')}
                            <span className='ms-2'>
                              {column.isSorted &&
                                (column.isSortedDesc ? (
                                  <i className='fas fa-angle-down' />
                                ) : (
                                  <i className='fas fa-angle-up' />
                                ))}
                            </span>
                          </th>
                          {checkNoSubActions && elementCustomsColumn}
                        </React.Fragment>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className={tbodyClass}>
                {rows.map((row: any, index) => {
                  prepareRow(row)
                  const isHighlighted = highlightRowIds.includes(row.original.id)
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={index}
                      className={clsx({'bg-highlight-row': isHighlighted})}
                    >
                      {useCheckBox && (
                        <td>
                          {row.values.canCheck && (
                            <div
                              className={clsx(
                                'form-check form-check-sm form-check-custom form-check-solid ps-4',
                                {
                                  'opacity-50 pe-none': disabledCheckBox,
                                }
                              )}
                            >
                              <InputCheckNoFormik
                                checked={
                                  masterChecked ||
                                  selectedRows.find((item: any) => item.id === row.id)
                                }
                                onChange={() => handleSelect(row)}
                              />
                            </div>
                          )}
                        </td>
                      )}
                      {row.cells.map((cell: any, cellIndex: number) => {
                        return (
                          <td
                            key={cellIndex}
                            {...cell.getCellProps([
                              {
                                className: cell.column.cellClassName,
                              },
                            ])}
                          >
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
                <div ref={bottomRef} className='w-1px h-1px' />
              </tbody>
            </table>
          </div>

          {/* end::Table */}
          {/* end::Table container */}
          {/* begin::Paginate */}

          {/* end::Paginate */}
        </div>
        <div
          className={clsx(
            'table-pagination-bar row py-1 bg-white position-sticky align-items-center gap-6',
            {
              'justify-content-between': isInModal && width >= LAPTOP_MIN_WIDTH,
            }
          )}
        >
          <div
            className={clsx('col-12 col-lg-2 min-w-270px d-flex align-items-center', {
              'justify-content-center justify-content-lg-start': !isInModal,
              'justify-content-center justify-content-sm-start': isInModal,
            })}
          >
            {paginationInfo && usePagination && !isEmpty(rows) && (
              <>
                <PageSize
                  event={(pageSize: any) => handlePageSizeChange(pageSize)}
                  pageSize={paginationInfo.page_size}
                />
                <div className='ms-4 text-muted'>
                  {intl.formatMessage(
                    {id: 'SHOWING_TOTAL_ROWS'},
                    {current: rows?.length, total: paginationInfo.total}
                  )}
                </div>
              </>
            )}
          </div>
          <div
            className={clsx('col-12 col-lg-6 d-flex align-items-center mt-4 mt-sm-0', {
              'justify-content-center justify-content-lg-start': !isInModal,
              'justify-content-center justify-content-lg-end': isInModal,
            })}
          >
            {paginationInfo && usePagination && (
              <PaginateWidget
                onPageChange={(page: number) => handlePageChange(page)}
                pagination={paginationInfo}
              />
            )}
          </div>
        </div>
        {/* end::Body */}
      </>
    )
  }
)
export {Table}
